<template>
  <a-modal
    v-if="show"
    v-model="show"
    :footer="null"
    :width="width"
    @cancel="close"
    :closable="!submitting"
    class="payment-modal"
    :maskClosable="false"
  >
    <template slot="title">
      <span class="title-text">
        <font-awesome-icon class="anticon" :icon="['far', 'money-bill-alt']" />
        {{ labels.payment.title }}
      </span>
    </template>

    <a-row
      type="flex"
      justify="center"
      :gutter="[24, 24]"
      class="payment-modal-content"
    >
      <a-col :xs="24" :sm="24" :md="18" :lg="18">
        <a-table
          v-if="dataSource.length"
          :dataSource="dataSource"
          :pagination="false"
          :columns="columns"
          :rowKey="'key'"
          :size="'small'"
          :bordered="false"
          :class="'payment-modal-table'"
          :rowClassName="
            (row) =>
              `table-row ${
                markedKeys.includes(row.key) ? ' marked' : ''
              }`.trim()
          "
          :expandIcon="expandIcon"
          :expandRowByClick="false"
          :defaultExpandAllRows="true"
          :expandedRowKeys="dataSource.map((item) => item.key)"
        />
      </a-col>
      <a-col :xs="24" :sm="24" :md="6" :lg="6">
        <a-card :border="true" class="payment-modal-total">
          <a-button
            type="primary"
            class="payment-modal-total-button-pay"
            @click="handlePayTicket"
            :disabled="submitting"
            :loading="submitting"
            block
          >
            {{ labels.payment.payTicket }}
          </a-button>
          <a-divider :style="{ marginTop: 10, marginBottom: 10 }" />
          <div class="payment-modal-total-container">
            <span class="payment-modal-total-label"
              >{{ labels.payment.totalTicket }}
            </span>
            <span class="payment-modal-total-text">{{
              total | formatMoney("$ ", 2, ",", ".")
            }}</span>
          </div>
        </a-card>
      </a-col>
    </a-row>
  </a-modal>
</template>

<script>
import EventBus, { ListEvents } from "../event";
import labels from "@/utils/labels";
import { mapState } from "vuex";
import _ from "lodash";
import { generateUniqueID } from "../utils/utils";
import EditableCell from "@/components/EditableCell.vue";
export default {
  name: "BuyPaymentModal",
  emits: ["pay", "close", "cellChange"],
  props: {
    labels: {
      type: Object,
      default: () => labels,
    },
    dataSource: {
      type: Array,
      default: () => [],
    },
    total: {
      type: Number,
      default: 0,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    markedKeys: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      show: false,
    };
  },
  created() {
    EventBus.$on(ListEvents.open_payment_modal, this.open);
    EventBus.$on(ListEvents.close_payment_modal, this.close);
  },
  beforeDestroy() {
    EventBus.$off(ListEvents.open_payment_modal);
    EventBus.$off(ListEvents.close_payment_modal);
  },
  computed: {
    ...mapState("layout", ["screenWidth"]),
    isMobile() {
      return this.screenWidth < 768;
    },
    width() {
      return this.screenWidth < 992 ? "96%" : 992;
    },
    columns() {
      return [
        {
          title: this.labels.table.play,
          dataIndex: "type",
          key: "type",
          width: "35%",
          customRender: (text, record) => {
            if (record?.isLottery) {
              const lotteryName = record.secondLottery?.id
                ? `${record.lottery.name} - ${record.secondLottery.name}`
                : record.lottery.name;
              return {
                children: <span class="text-hover">{lotteryName}</span>,
                attrs: {
                  colSpan: 4,
                },
              };
            }
            return (
              this.labels.form.playTypes[text] ||
              this.$options.filters.ucfirst(text)
            );
          },
        },
        {
          title: this.labels.table.numbers,
          dataIndex: "numbers",
          key: "numbers",
          width: "35%",
          align: "center",
          customRender: (numbers, record) => {
            if (record.isLottery) {
              return {
                attrs: {
                  colSpan: 0,
                },
              };
            }
            return numbers;
          },
        },
        {
          title: this.labels.table.amount,
          dataIndex: "amount",
          key: "amount",
          width: "30%",
          align: "right",
          customRender: (amount, record) => {
            if (record.isLottery) {
              return {
                attrs: {
                  colSpan: 0,
                },
              };
            }
            // editable cell
            return {
              children: (
                <EditableCell
                  modelValue={amount}
                  onChange={(value) =>
                    this.onCellChange(record.key, "amount", value)
                  }
                />
              ),
            };
          },
        },
      ];
    },
  },
  methods: {
    uuid() {
      return generateUniqueID();
    },
    close() {
      this.$emit("close");
      this.show = false;
    },
    open() {
      if (_.cloneDeep(this.dataSource).length) this.show = true;
    },
    async handlePayTicket() {
      try {
        const data = _.cloneDeep(this.dataSource).reduce((acc, curr) => {
          return [...acc, ...curr.children];
        }, []);
        this.$emit("pay", data);
      } catch (error) {
        console.error(error);
      }
    },
    expandIcon({ expandable }) {
      return expandable ? "" : " ";
    },
    onCellChange(key, dataIndex, value) {
      this.$emit("cellChange", key, dataIndex, value);
    },
  },
};
</script>
<style lang="scss">
@import "~@/assets/scss/variable";
.payment-modal {
  .ant-modal-content {
    background-color: var(--modal-bg) !important;
    border-radius: 0;
    border: 1px solid var(--modal-shadow) !important;
    box-shadow: 0 1px 3px 3px var(--modal-shadow) !important;
  }
  .ant-modal-header {
    background-color: transparent;
    border-color: var(--modal-shadow);
    .title-text {
      color: var(--text);
      font-size: 18px;
      .anticon {
        color: var(--text);
      }
    }
  }
  &-total {
    color: var(--text);
    font-size: 14px;
    user-select: none;
    background-color: transparent;
    &-button-pay {
      background-color: var(--btn-bg-primary);
      border-color: var(--btn-bc-primary);
      color: var(--btn-text-primary);
      border-radius: 0;
      font-weight: 600;
      &:hover,
      &:focus,
      &:active {
        background-color: var(--btn-bg-primary);
        border-color: var(--btn-bc-primary);
        color: var(--btn-text-primary);
      }
    }
    &-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    &-label {
      font-weight: 600;
      margin-right: 5px;
    }
    &-text {
      font-weight: 600;
      border-bottom: 1px solid var(--text);
    }
  }
  .ant-table {
    &:hover {
      transition: 0.5s ease;
      background: unset;
    }
    tr.ant-table-row:hover {
      transition: 0.5s ease;
      background: var(--table-hover) !important;
    }
    tr:nth-child(2n) {
      transition: 0.5s ease;
      background: var(--table-striped) !important;
    }
    tr.marked {
      color: var(--text);
      background: var(--danger-lighter) !important;
      font-weight: 600;
    }
    tr.table-row-disabled,
    tr.table-row-disabled:hover {
      color: var(--table-disabled) !important;
      background: var(--table-disabled) !important;
      font-weight: 600;
    }
    .ant-table-tbody > tr.ant-table-row:hover > td {
      transition: 0.5s ease;
      background: none !important;
    }
    &-tbody,
    &-thead {
      color: var(--text) !important;
      tr {
        th {
          color: var(--text) !important;
          background-color: transparent !important;
        }
      }
    }
    &-footer {
      background: transparent;
    }
  }
  .ant-modal-close {
    color: var(--text-muted) !important;
    &:hover {
      color: var(--text) !important;
    }
  }
}
.success-payment-modal {
  .ant-modal-content {
    background-color: var(--modal-bg) !important;
    box-shadow: 0 1px 3px 3px var(--modal-shadow) !important;
    .ant-modal-confirm-title {
      color: var(--text) !important;
    }
    .ant-modal-confirm-content {
      color: var(--text) !important;
    }
    .ant-modal-confirm-btns {
      .ant-btn {
        color: var(--btn-text-secondary);
        border-color: var(--btn-bc-secondary);
        background-color: var(--btn-bg-secondary);
        &:hover,
        &:focus,
        &:active {
          color: var(--btn-text-secondary);
          border-color: var(--btn-bc-secondary);
          background-color: var(--btn-bg-secondary);
        }
        &.ant-btn-primary {
          color: var(--btn-text-primary);
          border-color: var(--btn-bc-primary);
          background-color: var(--btn-bg-primary);
          &:hover {
            color: var(--btn-text-primary);
            border-color: var(--btn-bc-primary);
            background-color: var(--btn-bg-primary);
          }
        }
      }
    }
  }
}
</style>
